import axiosInstance from "@/axios/axiosInstance";
import {
  LoginCredentials,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  LoginResponse,
  RegisterResponse,
  RegisterRequest,
  OAuthResponse,
  AccountRestorationResponse,
  EmailConfirmationRequest,
  EmailConfirmationResponse,
  ResendConfirmationRequest,
  ResendConfirmationResponse,
  ExternalSignupRequest,
  ExternalSignupResponse,
  ExternalLoginRequest,
  ExternalLoginResponse,
  ContractorSignupRequest,
  ContractorSignupResponse,
  VerifyPhoneOtpRequest,
  ResendPhoneOtpRequest,
  PhoneOtpResponse,
} from "@/interface/auth.types";
import { SignupFormValues } from "@/schema/signup";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";

// Extract a user-friendly error message from API error formats
export const parseApiErrorMessage = (error: any): string => {
  const errorMessage = error?.response?.data?.errors?.[0]?.message;
  if (errorMessage) return errorMessage;
  const fallbackMessage = "Something Went Wrong";
  const anyError = error as any;
  const data = anyError?.response?.data ?? anyError;

  if (!data) return fallbackMessage;
  if (typeof data === "string") return data || fallbackMessage;

  // Common API shape: { message: string, errors: Array<{ message | field }> }
  try {
    const anyData = data as any;
    const message = anyData?.message as string | undefined;
    const errors = Array.isArray(anyData?.errors) ? anyData.errors : [];
    const firstError = errors?.[0] ?? {};
    const firstErrorMessage =
      // prefer explicit message
      (firstError?.message as string | undefined) ||
      // sometimes APIs put message in field when not tied to a real field
      (firstError?.field as string | undefined);

    return firstErrorMessage || message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

export class AuthService {
  private baseUrl = "/Identity";

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/Login`,
        credentials
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async register(userData: SignupFormValues): Promise<RegisterResponse> {
    try {
      const payload = {
        userType: userData.type === "contractor" ? 2 : 1,
        fullName: userData.name,
        email: userData.email,
        password: userData.password,
      };
      const response = await axiosInstance.post(
        `${this.baseUrl}/Register`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Contractor-specific signup. Unlike Register (email OTP), the backend
   * sends an SMS OTP to mobilePhone and returns a sessionId consumed by
   * VerifyPhoneOtp / ResendPhoneOtp. No toast here — the signup form
   * renders errors inline (mirrors register()).
   */
  async registerContractor(
    data: ContractorSignupRequest
  ): Promise<ContractorSignupResponse> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/RegisterContractor`,
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async verifyPhoneOtp(data: VerifyPhoneOtpRequest): Promise<PhoneOtpResponse> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/VerifyPhoneOtp`,
        data
      );
      return response.data;
    } catch (error) {
      showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
      throw error;
    }
  }

  async resendPhoneOtp(data: ResendPhoneOtpRequest): Promise<PhoneOtpResponse> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/ResendPhoneOtp`,
        data
      );
      return response.data;
    } catch (error) {
      showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
      throw error;
    }
  }

  async initiateOAuth(provider: string): Promise<OAuthResponse> {
    try {
      const response = await axiosInstance.get(
        `${this.baseUrl}/OAuth/${provider}`
      );
      return response.data;
    } catch (error) {
      showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
      throw error;
    }
  }

  async restoreAccount(token: string): Promise<AccountRestorationResponse> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/Account/Restore`,
        { token }
      );
      return response.data;
    } catch (error) {
      showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
      throw error;
    }
  }

  async verifyEmailOtp(
    data: EmailConfirmationRequest
  ): Promise<EmailConfirmationResponse> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/VerifyEmailOtp`,
        data
      );
      return response.data;
    } catch (error) {
      showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
      throw error;
    }
  }

  async resendConfirmationEmailOtp(
    data: ResendConfirmationRequest
  ): Promise<ResendConfirmationResponse> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/ResendConfirmationEmailOtp`,
        data
      );
      return response.data;
    } catch (error) {
      showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
      throw error;
    }
  }
}

// Legacy functions for backward compatibility
export const loginUser = async (data: LoginCredentials) => {
  const authService = new AuthService();
  return authService.login(data);
};

export const signup = async (data: SignupFormValues) => {
  const authService = new AuthService();
  return authService.register(data);
};

export const registerContractor = async (data: ContractorSignupRequest) => {
  const authService = new AuthService();
  return authService.registerContractor(data);
};

export const verifyPhoneOtp = async (data: VerifyPhoneOtpRequest) => {
  const authService = new AuthService();
  return authService.verifyPhoneOtp(data);
};

export const resendPhoneOtp = async (data: ResendPhoneOtpRequest) => {
  const authService = new AuthService();
  return authService.resendPhoneOtp(data);
};

export const confirmSignup = async (payload: {
  uid?: string;
  email?: string;
  code: string;
}) => {
  try {
    const endpoint = payload.uid ? `/signup/confirm` : `/signup/confirm/email`;
    const requestPayload = payload.uid
      ? { uid: payload.uid, code: payload.code }
      : { email: payload.email, code: payload.code };

    const response = await axiosInstance.post(endpoint, requestPayload);
    return response?.data as { token: string; user: unknown };
  } catch (error) {
    showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
    throw error;
  }
};

export const resendConfirmation = async (payload: {
  uid?: string;
  email?: string;
}) => {
  try {
    const endpoint = payload.uid ? `/signup/resend` : `/signup/resend/email`;
    const requestPayload = payload.uid
      ? { uid: payload.uid }
      : { email: payload.email };

    const response = await axiosInstance.post(endpoint, requestPayload);
    return response?.data as { ok: boolean };
  } catch (error) {
    showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
    throw error;
  }
};

export const forgotPassword = async (
  data: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> => {
  try {
    const response = await axiosInstance.post(`/identity/ForgotPassword`, data);
    return response?.data;
  } catch (error) {
    showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
    throw error;
  }
};

export const resetPassword = async (
  data: ResetPasswordRequest
): Promise<ResetPasswordResponse> => {
  try {
    const response = await axiosInstance.post(`/identity/ResetPassword`, data);
    return response?.data;
  } catch (error) {
    showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
    throw error;
  }
};

export const verifyEmailOtp = async (
  data: EmailConfirmationRequest
): Promise<EmailConfirmationResponse> => {
  const authService = new AuthService();
  return authService.verifyEmailOtp(data);
};

export const resendConfirmationEmailOtp = async (
  data: ResendConfirmationRequest
): Promise<ResendConfirmationResponse> => {
  const authService = new AuthService();
  return authService.resendConfirmationEmailOtp(data);
};

export const externalSignup = async (
  data: ExternalSignupRequest
): Promise<ExternalSignupResponse> => {
  try {
    const response = await axiosInstance.post(`/Identity/ExternalSignup`, data);

    // Check if response is successful and has expected structure
    if (
      response.status === 200 &&
      response.data?.data?.isSuccess !== undefined
    ) {
      // Note: Cookie setting is handled client-side in useSignup hook
      return response.data;
    } else {
      throw new Error("Unexpected response structure from external signup");
    }
  } catch (error) {
    console.error("External signup failed:", error);
    throw error;
  }
};

export const externalLogin = async (
  data: ExternalLoginRequest
): Promise<ExternalLoginResponse> => {
  try {
    const response = await axiosInstance.post(`/Identity/ExternalLogin`, data);
    return response.data;
  } catch (error) {
    console.error("External login failed:", error);
    throw error;
  }
};

export const logout = async (): Promise<void> => {
  try {
    await axiosInstance.post(`/Identity/Logout`);
  } catch (error) {
    showToast(TOAST_TYPES.error, parseApiErrorMessage(error));
    throw error;
  }
};
